<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ImageGrid> in <template>
import { ImageGrid } from '@/ui-kit';
import type { ImageGridItem } from '@/ui-kit/components/image-grid/types';
// biome-ignore lint/correctness/noUnusedImports: used via <component :is> in <template>
import ClassicPortraitGalleryFeed from './ClassicPortraitGalleryFeed.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryFilter> in <template>
import ClassicPortraitGalleryFilter from './ClassicPortraitGalleryFilter.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryGroupBlock> in <template>
import ClassicPortraitGalleryGroupBlock from './ClassicPortraitGalleryGroupBlock.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryRows> in <template>
import ClassicPortraitGalleryRows from './ClassicPortraitGalleryRows.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryStage> in <template>
import ClassicPortraitGalleryStage from './ClassicPortraitGalleryStage.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <component :is> in <template>
import ClassicPortraitGalleryZones from './ClassicPortraitGalleryZones.vue';
import {
  type ClassicPortraitGalleryLayout,
  type ClassicPortraitGalleryOrientation,
  type ClassicPortraitGalleryPages,
  type ClassicPortraitGalleryRail,
  type ClassicPortraitGalleryFilter as FilterValue,
  filterGalleryItems,
  type GalleryEntry,
  galleryKindCounts,
  isMixedItemReady,
  type MixedPortraitGalleryItem,
  mixedItemRatio,
  resolveGalleryPlan,
} from './classic-mixed-portrait-gallery';
// biome-ignore lint/correctness/noUnusedImports: used via <PortraitCardOverlay> in <template>
import PortraitCardOverlay from './PortraitCardOverlay.vue';

interface Props {
  // Tabs that narrow the gallery to one kind, shown when both kinds are
  // present.
  filter?: boolean;
  generateMoreDisabled: boolean;
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  // The paged layout shows the group portraits first among its cells, a bit
  // larger than the singles. The other layouts put them in a block of their
  // own before the engine.
  groupItems?: MixedPortraitGalleryItem[];
  items: MixedPortraitGalleryItem[];
  layout?: ClassicPortraitGalleryLayout;
  orientation?: ClassicPortraitGalleryOrientation;
  // The geometry of the old paged layout; the `pages` layout needs it.
  pages?: ClassicPortraitGalleryPages | null;
  rail?: Partial<ClassicPortraitGalleryRail> | null;
  selectedUuids: string[];
  showGenerateMore: boolean;
  // Forces the stage on or off; null lets the layout decide.
  showPreview?: boolean | null;
}

interface ScrollTarget {
  canScroll: () => boolean;
  isItemVisible: (uuid: string) => boolean | null;
  scrollToItem: (uuid: string) => boolean;
}

const props = withDefaults(defineProps<Props>(), {
  filter: true,
  groupItems: () => [],
  layout: 'strip',
  orientation: 'landscape',
  pages: null,
  rail: null,
  showPreview: null,
});
const emit = defineEmits<{
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  generateMore: [];
  itemClick: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  switchSlide: [visibleUuids: string[]];
  toggleSelection: [
    event: MouseEvent,
    item: MixedPortraitGalleryItem,
    isSelected: boolean,
  ];
  tryAgain: [item: MixedPortraitGalleryItem];
}>();

const engine = ref<ScrollTarget | null>(null);
const activeUuid = ref<string | null>(null);
const activeSource = shallowRef<unknown>(null);
const filter = ref<FilterValue>('all');

// The paged layout needs its geometry; without it the strip stands in.
const layout = computed<ClassicPortraitGalleryLayout>(() =>
  props.layout === 'pages' && !props.pages ? 'strip' : props.layout
);
const pagesGeometry = computed(() =>
  layout.value === 'pages' ? props.pages : null
);
const hasGroupBlock = computed(
  () => props.groupItems.length > 0 && !pagesGeometry.value
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const pagedItems = computed(() => [...props.groupItems, ...props.items]);
const groupTileHeight = ref(0);
// The block takes the shape of its first tile: as wide as that tile needs at
// the height of the block in landscape, and a taller row for a tall tile in
// portrait orientation.
const groupBlockStyle = computed<Record<string, string>>(() => {
  const lead = props.groupItems[0];
  if (!(lead && hasGroupBlock.value)) {
    return {};
  }
  const ratio = mixedItemRatio(lead);
  const style: Record<string, string> = {
    '--cmg-group-row': ratio < 1 ? '46%' : '32%',
  };
  if (groupTileHeight.value > 0) {
    style['--cmg-group-column'] = `min(calc(${Math.round(
      groupTileHeight.value * ratio
    )}px + var(--cmg-group-chrome)), 50%)`;
  }
  return style;
});
const plan = computed(() =>
  resolveGalleryPlan(layout.value, props.orientation, {
    preview: props.showPreview ?? undefined,
    rail: props.rail,
  })
);
const counts = computed(() => galleryKindCounts(props.items));
const showFilter = computed(
  () =>
    props.filter &&
    layout.value !== 'pages' &&
    counts.value.group > 0 &&
    counts.value.individual > 0
);
watch(showFilter, (shown) => {
  if (!shown) {
    filter.value = 'all';
  }
});

const visibleItems = computed(() =>
  showFilter.value ? filterGalleryItems(props.items, filter.value) : props.items
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const entries = computed<GalleryEntry[]>(() =>
  visibleItems.value.map((item) => ({ index: props.items.indexOf(item), item }))
);

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rootStyle = computed(() => ({
  ...(pagesGeometry.value
    ? {
        '--ImageGallery--js-visible-item-count': String(
          pagesGeometry.value.layout.columns
        ),
      }
    : { '--cmg-dock': plan.value.dock }),
  ...groupBlockStyle.value,
}));
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rootClasses = computed(() => {
  const preset = pagesGeometry.value?.preset;
  return [
    `ClassicMixedPortraitGallery--${props.orientation}`,
    `ClassicMixedPortraitGallery--${plan.value.preview ? 'with' : 'without'}-preview`,
    `ClassicMixedPortraitGallery--${layout.value}`,
    `ClassicMixedPortraitGallery--engine-${pagesGeometry.value ? 'pages' : plan.value.engine}`,
    showFilter.value && 'ClassicMixedPortraitGallery--filtered',
    hasGroupBlock.value && 'ClassicMixedPortraitGallery--with-group',
    preset && `ClassicMixedPortraitGallery--pages-${preset}`,
    // The legacy .ImageGallery stylesheet computes item width, gaps, arrow
    // size and shadow space for these two presets — the same numbers the old
    // galleries rendered with.
    preset === 'carousel' && 'ImageGallery',
    preset === 'grid' && 'ImageGallery ImageGallery--grid-mode',
  ];
});

const activeItem = computed(
  () =>
    visibleItems.value.find((item) => item.source === activeSource.value) ??
    visibleItems.value.find((item) => item.uuid === activeUuid.value) ??
    visibleItems.value[0] ??
    null
);
const activeIndex = computed(() =>
  activeItem.value ? props.items.indexOf(activeItem.value) : -1
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const activePosition = computed(() =>
  activeItem.value ? visibleItems.value.indexOf(activeItem.value) + 1 : 0
);

// Focus follows the item's source object across a retry that replaces its
// uuid; otherwise it starts on the first selected or ready item of the
// list on screen.
watch(
  () => [
    visibleItems.value.map((item) => item.uuid).join('|'),
    props.selectedUuids.join('|'),
  ],
  () => {
    const bySource = visibleItems.value.find(
      (item) => item.source === activeSource.value
    );
    if (bySource) {
      activeUuid.value = bySource.uuid;
      return;
    }
    if (visibleItems.value.some((item) => item.uuid === activeUuid.value)) {
      return;
    }
    const next =
      visibleItems.value.find((item) =>
        props.selectedUuids.includes(item.uuid)
      ) ??
      visibleItems.value.find(isMixedItemReady) ??
      visibleItems.value[0];
    activeUuid.value = next?.uuid ?? null;
    activeSource.value = next?.source ?? null;
  },
  { immediate: true }
);

function asMixed(item: ImageGridItem): MixedPortraitGalleryItem {
  return item as MixedPortraitGalleryItem;
}

function isGroupItem(item: ImageGridItem): boolean {
  return asMixed(item).kind === 'group';
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function pagedItemScale(item: ImageGridItem): number {
  return isGroupItem(item) ? (pagesGeometry.value?.groupScale ?? 1) : 1;
}

// The ratio of a group portrait is the output size of its job, known at
// the 202: its cell takes the final shape before the image arrives.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function pagedItemRatioKnown(item: ImageGridItem): boolean {
  return isGroupItem(item);
}

function isSelected(item: MixedPortraitGalleryItem): boolean {
  return props.selectedUuids.includes(item.uuid);
}

function focusItem(item: MixedPortraitGalleryItem): void {
  activeUuid.value = item.uuid;
  activeSource.value = item.source;
  emit('switchSlide', [item.uuid]);
}

function toggleItem(
  item: MixedPortraitGalleryItem,
  index: number,
  event: MouseEvent
): void {
  if (!isMixedItemReady(item)) {
    event.preventDefault();
    return;
  }
  emit('itemClick', item, index, event);
  if (!event.defaultPrevented) {
    emit('toggleSelection', event, item, !isSelected(item));
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function activateStage(event: MouseEvent): void {
  if (activeItem.value) {
    toggleItem(activeItem.value, activeIndex.value, event);
  }
}

// With a stage, a tile only moves the focus; selection happens on the stage.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function activateTile(
  item: MixedPortraitGalleryItem,
  index: number,
  event: MouseEvent
): void {
  if (!plan.value.preview) {
    toggleItem(item, index, event);
    return;
  }
  focusItem(item);
  emit('itemClick', item, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function activatePagedCell(
  item: ImageGridItem,
  index: number,
  event: MouseEvent
): void {
  const mixed = asMixed(item);
  emit('itemClick', mixed, index, event);
  if (plan.value.preview && isMixedItemReady(mixed)) {
    focusItem(mixed);
    event.preventDefault();
  }
}

function canScroll(): boolean {
  return engine.value?.canScroll() ?? false;
}

function isItemVisible(uuid: string): boolean {
  return engine.value?.isItemVisible(uuid) ?? false;
}

function scrollToItem(uuid: string): void {
  engine.value?.scrollToItem(uuid);
}

defineExpose({ canScroll, isItemVisible, scrollToItem });
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery Portraits__gallery"
    :class="rootClasses"
    :style="rootStyle"
    data-testid="classic-mixed-portrait-gallery"
    :data-layout="layout"
    :data-engine="pagesGeometry ? 'pages' : plan.engine"
    :data-filter="showFilter ? filter : 'none'"
    :data-screen-orientation="orientation"
    :data-show-preview="plan.preview ? 'true' : 'false'"
  >
    <ClassicPortraitGalleryFilter
      v-if="showFilter"
      v-model="filter"
      :counts="counts"
    />

    <ClassicPortraitGalleryGroupBlock
      v-if="hasGroupBlock"
      class="ClassicMixedPortraitGallery__group"
      :items="groupItems"
      :orientation="orientation"
      :selected-uuids="selectedUuids"
      :get-selected-portrait-count="getSelectedPortraitCount"
      @activate="toggleItem"
      @item-load="(item, index, event) => emit('itemLoad', item, index, event)"
      @tile-height="(height) => (groupTileHeight = height)"
      @try-again="emit('tryAgain', $event)"
      @count-decrement="(event, item) => emit('countDecrement', event, item)"
      @count-increment="(event, item) => emit('countIncrement', event, item)"
    />

    <div
      v-if="entries.length === 0 && !(pagesGeometry && groupItems.length > 0)"
      class="ClassicMixedPortraitGallery__empty"
      data-testid="classic-mixed-gallery-empty"
    >
      <i class="ClassicMixedPortraitGallery__empty-spinner kiosk-spinner" />
      <span>{{ $t('Portraits.placeholderGenerating') }}</span>
    </div>

    <div v-else class="ClassicMixedPortraitGallery__body">
      <ClassicPortraitGalleryStage
        v-if="plan.preview && activeItem"
        :item="activeItem"
        :index="activeIndex"
        :position="activePosition"
        :total="entries.length"
        :selected="isSelected(activeItem)"
        :count="getSelectedPortraitCount(activeItem)"
        @activate="activateStage"
        @item-load="emit('itemLoad', activeItem, activeIndex, $event)"
        @try-again="emit('tryAgain', activeItem)"
        @count-decrement="emit('countDecrement', $event, activeItem)"
        @count-increment="emit('countIncrement', $event, activeItem)"
      />

      <ImageGrid
        v-if="pagesGeometry"
        ref="engine"
        class="ClassicMixedPortraitGallery__pages"
        data-testid="classic-mixed-gallery-pages"
        :items="pagedItems"
        :item-scale="pagedItemScale"
        :item-ratio-known="pagedItemRatioKnown"
        :layout="pagesGeometry.layout"
        :flow="pagesGeometry.flow"
        :sizing="pagesGeometry.sizing"
        :cell-width="pagesGeometry.cellWidth"
        :cell-height="pagesGeometry.cellHeight"
        :cell-aspect-ratio="pagesGeometry.cellAspectRatio"
        :fit="pagesGeometry.fit"
        :gap="pagesGeometry.gap"
        :cell-radius="pagesGeometry.cellRadius"
        :arrow-scale="pagesGeometry.arrowScale"
        :selected-uuids="selectedUuids"
        state-class-prefix="Portraits"
        :show-generate-more="showGenerateMore"
        :generate-more-disabled="generateMoreDisabled"
        @item-click="activatePagedCell"
        @item-load="(item, index, event) => emit('itemLoad', asMixed(item), index, event)"
        @toggle-selection="(event, item, selected) => emit('toggleSelection', event, asMixed(item), selected)"
        @visible-items-change="(uuids) => emit('switchSlide', uuids)"
        @generate-more="emit('generateMore')"
      >
        <template #default="{ item: pagedItem }">
          <!-- A loaded group image shows that it is a group; the mark only
               names the tile while it has no image. -->
          <div
            v-if="isGroupItem(pagedItem) && !isMixedItemReady(asMixed(pagedItem))"
            class="ClassicMixedPortraitGallery__group-mark"
            data-testid="portraits-group-mark"
          >
            {{ $t('Portraits.stageKindGroup') }}
          </div>
          <PortraitCardOverlay
            class-prefix="PortraitImageGrid"
            :group="isGroupItem(pagedItem)"
            :portrait="pagedItem"
            :selected="isSelected(asMixed(pagedItem))"
            :selected-count="getSelectedPortraitCount(asMixed(pagedItem))"
            @try-again="emit('tryAgain', asMixed(pagedItem))"
            @count-decrement="emit('countDecrement', $event, asMixed(pagedItem))"
            @count-increment="emit('countIncrement', $event, asMixed(pagedItem))"
          />
        </template>
      </ImageGrid>

      <ClassicPortraitGalleryRows
        v-else-if="plan.engine === 'rows'"
        ref="engine"
        :entries="entries"
        :columns="plan.rail.columns"
        :max-columns="plan.rail.maxColumns"
        :max-row-fill="plan.rail.maxRowFill"
        :section-columns="plan.rail.sectionColumns"
        :rows="plan.rail.rows"
        :gap="plan.rail.gap"
        :portrait-ratio="plan.rail.portraitRatio"
        :controls="plan.rail.controls"
        :orientation="orientation"
        :sections="plan.sections"
        :section-order="plan.sectionOrder"
        :active-uuid="plan.preview ? activeItem?.uuid : null"
        :selected-uuids="selectedUuids"
        :selection-on-tile="!plan.preview"
        :get-selected-portrait-count="getSelectedPortraitCount"
        :show-generate-more="showGenerateMore"
        :generate-more-disabled="generateMoreDisabled"
        @activate="activateTile"
        @item-load="(item, index, event) => emit('itemLoad', item, index, event)"
        @try-again="emit('tryAgain', $event)"
        @count-decrement="(event, item) => emit('countDecrement', event, item)"
        @count-increment="(event, item) => emit('countIncrement', event, item)"
        @generate-more="emit('generateMore')"
      />

      <component
        :is="plan.engine === 'zones' ? ClassicPortraitGalleryZones : ClassicPortraitGalleryFeed"
        v-else
        ref="engine"
        :entries="entries"
        :plan="plan"
        :active-uuid="plan.preview ? activeItem?.uuid : null"
        :selected-uuids="selectedUuids"
        :selection-on-tile="!plan.preview"
        :get-selected-portrait-count="getSelectedPortraitCount"
        :show-generate-more="showGenerateMore"
        :generate-more-disabled="generateMoreDisabled"
        @activate="activateTile"
        @item-load="(item, index, event) => emit('itemLoad', item, index, event)"
        @switch-slide="(uuids) => emit('switchSlide', uuids)"
        @try-again="emit('tryAgain', $event)"
        @count-decrement="(event, item) => emit('countDecrement', event, item)"
        @count-increment="(event, item) => emit('countIncrement', event, item)"
        @generate-more="emit('generateMore')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Root: the filter tabs over the body. The body is the engine alone or,
   with a stage, stage + engine side by side in landscape and stage over
   engine in portrait orientation. A rem width (not a percentage) keeps a
   shrink-to-fit screen from collapsing the gallery, since the stage is a
   size container with no intrinsic width. Every tap target is at least
   --cmg-touch wide. */
.ClassicMixedPortraitGallery {
  --cmg-touch: max(60px, 2.2rem);
  --cmg-gap: 0.8rem;
  --cmg-radius: 0.8rem;
  --cmg-dock: 26%;

  display: grid;
  flex: 1 1 0;
  grid-template-rows: minmax(0, 1fr);
  gap: 0.9rem;
  width: 96rem;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  user-select: none;
}

.ClassicMixedPortraitGallery--filtered {
  grid-template-rows: auto minmax(0, 1fr);
}

/* The group block comes first: at the left in landscape, at the top in
   portrait orientation. In landscape its column is as wide as its tile
   needs at the full height of the gallery (--cmg-group-column, from the
   script), thus the tile is a bit larger than one single. The chrome is the
   padding and the border of the block. */
.ClassicMixedPortraitGallery--with-group {
  --cmg-group-gap: 1.6rem;
  --cmg-group-chrome: calc(1.6rem + 2px);
  column-gap: var(--cmg-group-gap);
}

.ClassicMixedPortraitGallery--with-group.ClassicMixedPortraitGallery--landscape {
  grid-template-columns: minmax(0, var(--cmg-group-column, 42%)) minmax(0, 1fr);
}

.ClassicMixedPortraitGallery--with-group.ClassicMixedPortraitGallery--portrait {
  grid-template-rows: minmax(0, var(--cmg-group-row, 32%)) minmax(0, 1fr);
}

.ClassicMixedPortraitGallery--portrait {
  width: 52rem;
}

.ClassicMixedPortraitGallery__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--cmg-gap);
  min-width: 0;
  min-height: 0;
}

.ClassicMixedPortraitGallery--with-preview.ClassicMixedPortraitGallery--landscape
  .ClassicMixedPortraitGallery__body {
  grid-template-columns: minmax(0, 1fr) minmax(0, var(--cmg-dock));
}

.ClassicMixedPortraitGallery--with-preview.ClassicMixedPortraitGallery--portrait
  .ClassicMixedPortraitGallery__body {
  grid-template-rows: minmax(0, 1fr) minmax(0, var(--cmg-dock));
}

.ClassicMixedPortraitGallery__empty {
  display: grid;
  place-content: center;
  place-items: center;
  gap: 1rem;
  font-size: 1.4rem;
  color: var(--Kiosk--font_color);
  text-align: center;
}

.ClassicMixedPortraitGallery__empty-spinner {
  --kiosk-spinner-size: 3rem;
  --kiosk-spinner-color: var(--Kiosk--font_color);
}

/* Pages: the ui-kit ImageGrid box the old PortraitImageGrid rendered in. */
.ClassicMixedPortraitGallery--pages.ClassicMixedPortraitGallery--without-preview {
  flex: var(--portrait-image-grid-flex, 0 1 auto);
  width: var(--portrait-image-grid-width, 96rem);
  height: var(--portrait-image-grid-height, 42rem);
}

.ClassicMixedPortraitGallery__pages {
  min-width: 0;
  min-height: 0;
}

/* Top left of the group cell: the checkmark takes the top right and the
   counter the bottom left. The mark stays over the placeholder and the
   error text of the cell. Its size follows the short side of the cell, thus
   a wide landscape cell gets the same mark as a tall cell. */
.ClassicMixedPortraitGallery__group-mark {
  --cmg-group-mark-unit: calc(
    min(var(--ImageGrid--item-width), var(--ImageGrid--item-height)) * 0.01
  );

  position: absolute;
  top: calc(var(--cmg-group-mark-unit) * 4);
  left: calc(var(--cmg-group-mark-unit) * 4);
  z-index: 4;
  max-width: calc(100% - var(--cmg-group-mark-unit) * 26);
  padding: 0.35em 0.8em;
  font-size: calc(var(--cmg-group-mark-unit) * 5.2);
  line-height: 1.2;
  color: var(--Kiosk--button_font_color);
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  background-color: var(--Kiosk--button_background_color);
  border-radius: 3rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

/* Carousel and vertical grid: the box is the legacy .ImageGallery area (cells
   plus the gap or shadow padding around them), so arrows and the + tile land
   where the old galleries put them. */
.ClassicMixedPortraitGallery--pages.ClassicMixedPortraitGallery--pages-carousel,
.ClassicMixedPortraitGallery--pages.ClassicMixedPortraitGallery--pages-grid {
  /* ImageGrid re-declares the --ImageGallery--* variables on every cell, so a
     cell fed `var(--ImageGallery--item-height)` would reference itself and
     go invalid; the geometry props read these aliases instead. */
  --cmg-legacy-item-width: var(--ImageGallery--item-width);
  --cmg-legacy-item-height: var(--ImageGallery--item-height);
  --cmg-legacy-item-radius: var(--ImageGallery--item-radius);
  --cmg-legacy-item-gap: var(--ImageGallery--item-gap);
  /* Room the selection glow needs: its blur plus its spread, both gap/5.
     The legacy grid calls this --ImageGallery--shadow-space; the carousel
     never defines one, so derive it. */
  --cmg-shadow-space: calc(var(--ImageGallery--item-gap) * 2 / 5);

  width: var(--ImageGallery--area-width);
  height: auto;
}

.ClassicMixedPortraitGallery--pages-carousel :deep(.ImageGrid),
.ClassicMixedPortraitGallery--pages-grid :deep(.ImageGrid) {
  box-sizing: border-box;
  --ImageGrid--arrow-size: var(--ImageGallery--arrow-size) !important;
}

/* ImageGrid's own selection glow reads --ImageGallery--item-gap, which it
   re-declares on the cell from the gap prop — a cycle that drops the shadow.
   Restate it from the alias, the same numbers the old galleries used. */
.ClassicMixedPortraitGallery--pages-carousel
  :deep(.ImageGrid__item--selected .ImageGrid__item-wrapper),
.ClassicMixedPortraitGallery--pages-grid
  :deep(.ImageGrid__item--selected .ImageGrid__item-wrapper) {
  box-shadow: var(
    --gallery-selected-shadow,
    0 0 calc(var(--cmg-legacy-item-gap) / 5)
      calc(var(--cmg-legacy-item-gap) / 5) rgba(var(--Kiosk--font_color-rgb), 0.7)
  );
}

/* The legacy list shrinks its three slides into the area width, which leaves
   five sixths of the gap between cards and a half gap around them. The half
   gap is split: the shadow space goes inside the scrolling box, where the
   selection glow needs room (it clips), the rest stays outside. */
.ClassicMixedPortraitGallery--pages-carousel :deep(.ImageGrid) {
  padding: calc(var(--ImageGallery--item-gap) / 2 - var(--cmg-shadow-space));
}

.ClassicMixedPortraitGallery--pages-carousel :deep(.ImageGrid__scrollable) {
  padding: var(--cmg-shadow-space);
}

.ClassicMixedPortraitGallery--pages-carousel :deep(.ImageGrid__page) {
  gap: calc(var(--ImageGallery--item-gap) * 5 / 6) !important;
}

/* Same split for the vertical grid: the shadow space belongs inside the
   scrolling box so the selected card's glow is not cut off. */
.ClassicMixedPortraitGallery--pages-grid :deep(.ImageGrid) {
  padding: 0;
}

.ClassicMixedPortraitGallery--pages-grid :deep(.ImageGrid__scrollable) {
  padding: var(--ImageGallery--shadow-space);
}
</style>
